import { useRef, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Library as LibraryIcon, Users, Cpu, DollarSign, Brush, Eraser, Undo2, Redo2, Film,
  Trash2, Send, Loader2, Image as ImageIcon, Upload, Wand2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { formatCredits } from '../utils/credits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV_ITEMS = [
  { id: 'generate', label: 'Generate', icon: Sparkles, path: '/generate' },
  { id: 'canvas', label: 'Canvas', icon: Brush, path: '/canvas' },
  { id: 'image-to-video', label: 'Img → Vid', icon: Film, path: '/image-to-video' },
  { id: 'library', label: 'Library', icon: LibraryIcon, path: '/library' },
  { id: 'community', label: 'Community', icon: Users, path: '/community' },
];

const PALETTE = [
  '#000000', '#FFFFFF', '#FF6B4A', '#E040A0', '#A855F7', '#4A7CFF',
  '#06B6D4', '#34B76F', '#FFD93D', '#92400E', '#EF4444', '#F97316',
];

const BRUSH_SIZES = [2, 4, 8, 16, 32, 64];

export default function SketchCanvas() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null);   // hidden offscreen mask layer (white where painted, black elsewhere)
  const containerRef = useRef(null);

  const [mode, setMode] = useState('sketch'); // 'sketch' | 'inpaint'
  const [baseImageUrl, setBaseImageUrl] = useState(null);
  const [baseImagePath, setBaseImagePath] = useState(null); // server path
  const [uploadingBase, setUploadingBase] = useState(false);

  const [tool, setTool] = useState('brush'); // brush | eraser
  const [color, setColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(8);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [history, setHistory] = useState([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [sending, setSending] = useState(false);
  const [prompt, setPrompt] = useState('');

  // Init canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const container = containerRef.current;
    const size = Math.min(container.clientWidth - 32, container.clientHeight - 32, 1024);
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Init offscreen mask canvas (same size)
    const mask = maskCanvasRef.current || document.createElement('canvas');
    mask.width = canvas.width;
    mask.height = canvas.height;
    const mctx = mask.getContext('2d');
    mctx.fillStyle = '#000000';
    mctx.fillRect(0, 0, mask.width, mask.height);
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';
    maskCanvasRef.current = mask;
    // Save initial state
    saveHistory();
  }, []);

  // Load base image into canvas when entering inpaint mode
  useEffect(() => {
    if (mode !== 'inpaint' || !baseImageUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Fit image to canvas while preserving aspect
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (canvas.width - w) / 2;
      const y = (canvas.height - h) / 2;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#0A0A0F';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, x, y, w, h);
      // Reset mask
      const mctx = maskCanvasRef.current.getContext('2d');
      mctx.fillStyle = '#000000';
      mctx.fillRect(0, 0, canvas.width, canvas.height);
      saveHistory();
    };
    img.src = baseImageUrl;
  }, [baseImageUrl, mode]);

  const uploadBaseImage = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploadingBase(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setBaseImagePath(res.data.path);
      setBaseImageUrl(URL.createObjectURL(file));
      toast.success('Image loaded — paint over what you want AI to fill');
    } catch (err) {
      toast.error('Upload failed');
    } finally { setUploadingBase(false); }
  };

  // Re-fill on bgColor change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Fill behind current strokes — restore latest then redraw bg under
    if (historyIdx < 0) {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [bgColor]);

  const saveHistory = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const snap = canvas.toDataURL();
    setHistory((h) => {
      const trimmed = h.slice(0, historyIdx + 1);
      const next = [...trimmed, snap].slice(-30); // cap at 30 snapshots
      setHistoryIdx(next.length - 1);
      return next;
    });
  };

  const restore = (snap) => {
    const canvas = canvasRef.current;
    if (!canvas || !snap) return;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
    };
    img.src = snap;
  };

  const undo = () => {
    if (historyIdx <= 0) return;
    const newIdx = historyIdx - 1;
    setHistoryIdx(newIdx);
    restore(history[newIdx]);
  };

  const redo = () => {
    if (historyIdx >= history.length - 1) return;
    const newIdx = historyIdx + 1;
    setHistoryIdx(newIdx);
    restore(history[newIdx]);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    saveHistory();
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startStroke = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e);
    if (mode === 'inpaint') {
      // Semi-transparent magenta overlay on visible canvas
      ctx.strokeStyle = tool === 'eraser' ? '#0A0A0F' : 'rgba(255, 64, 160, 0.65)';
      // Solid white/black on offscreen mask
      const mctx = maskCanvasRef.current.getContext('2d');
      mctx.strokeStyle = tool === 'eraser' ? '#000000' : '#FFFFFF';
      mctx.lineWidth = brushSize;
      mctx.beginPath();
      mctx.moveTo(x, y);
    } else {
      ctx.strokeStyle = tool === 'eraser' ? bgColor : color;
    }
    ctx.lineWidth = brushSize;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (mode === 'inpaint') {
      const mctx = maskCanvasRef.current.getContext('2d');
      mctx.lineTo(x, y);
      mctx.stroke();
    }
  };

  const endStroke = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    saveHistory();
  };

  const sendToStudio = async () => {
    if (!user) { navigate('/login'); return; }
    if (!prompt.trim()) {
      toast.error('Add a prompt to describe what you want');
      return;
    }
    setSending(true);
    try {
      if (mode === 'inpaint') {
        if (!baseImagePath) { toast.error('Upload a base image first'); setSending(false); return; }
        // Export the mask canvas
        const mask = maskCanvasRef.current;
        const maskBlob = await new Promise((resolve) => mask.toBlob(resolve, 'image/png'));
        const maskFd = new FormData();
        maskFd.append('file', maskBlob, `mask_${Date.now()}.png`);
        const maskRes = await axios.post(`${API}/upload`, maskFd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        const inpaintRes = await axios.post(`${API}/inpaint`, {
          base_image_path: baseImagePath,
          mask_image_path: maskRes.data.path,
          prompt: prompt,
          num_images: 1,
        }, { withCredentials: true });
        toast.success(inpaintRes.data.status === 'completed' ? 'Inpainted ✨' : 'Inpaint queued — heading to library');
        navigate('/library');
      } else {
        // Sketch mode: export canvas, navigate to /generate
        const canvas = canvasRef.current;
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        const fd = new FormData();
        fd.append('file', blob, `sketch_${Date.now()}.png`);
        const upRes = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        const params = new URLSearchParams({
          sketch_path: upRes.data.path,
          prompt: prompt,
        });
        navigate(`/generate?${params.toString()}`);
        toast.success('Sketch sent to Studio ✨');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex" data-testid="canvas-page">
      <Sidebar location={location} user={user} />
      <main className="flex-1 flex flex-col h-screen">
        <header className="h-16 border-b border-white/[0.05] flex items-center px-6 lg:px-10 shrink-0">
          <div>
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FF6B4A]">{mode === 'inpaint' ? 'Inpainting' : 'Sketch-to-Image'}</p>
            <h1 className="font-display text-base font-bold text-white" data-testid="canvas-heading">
              {mode === 'inpaint' ? 'Paint over what AI should replace.' : 'Draw it. Watch AI bring it to life.'}
            </h1>
          </div>
        </header>

        <div className="flex-1 flex overflow-hidden">
          {/* Tools */}
          <aside className="w-64 border-r border-white/[0.05] bg-[#08080C] p-4 overflow-y-auto space-y-5" data-testid="canvas-tools">
            {/* Mode toggle */}
            <div data-testid="mode-toggle">
              <div className="text-[10px] font-bold tracking-[0.3em] text-white/50 uppercase mb-2">Mode</div>
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={() => setMode('sketch')} data-testid="mode-sketch"
                  className={`p-2.5 rounded-lg flex items-center justify-center gap-1.5 text-xs transition-colors ${mode === 'sketch' ? 'bg-[#FF6B4A]/20 text-[#FF6B4A] border border-[#FF6B4A]/30' : 'bg-white/[0.04] text-white/60 hover:text-white border border-white/[0.05]'}`}>
                  <Brush className="w-3.5 h-3.5" /> Sketch
                </button>
                <button onClick={() => setMode('inpaint')} data-testid="mode-inpaint"
                  className={`p-2.5 rounded-lg flex items-center justify-center gap-1.5 text-xs transition-colors ${mode === 'inpaint' ? 'bg-[#E040A0]/20 text-[#E040A0] border border-[#E040A0]/30' : 'bg-white/[0.04] text-white/60 hover:text-white border border-white/[0.05]'}`}>
                  <Wand2 className="w-3.5 h-3.5" /> Inpaint
                </button>
              </div>
              <p className="text-[10px] text-white/40 mt-1.5 leading-snug">
                {mode === 'sketch' ? 'Draw a scene, AI brings it to life.' : 'Load image, paint area to replace.'}
              </p>
            </div>

            {/* Upload base image (Inpaint only) */}
            {mode === 'inpaint' && (
              <div data-testid="inpaint-upload">
                <div className="text-[10px] font-bold tracking-[0.3em] text-white/50 uppercase mb-2">Base Image</div>
                {baseImageUrl ? (
                  <div className="relative rounded-lg overflow-hidden border border-white/[0.08]">
                    <img src={baseImageUrl} alt="" className="w-full h-24 object-cover" />
                    <label className="absolute inset-0 bg-black/60 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-[10px] text-white font-bold uppercase tracking-wider cursor-pointer">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBaseImage(e.target.files[0])} data-testid="inpaint-base-input" />
                      Replace
                    </label>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-[#E040A0]/30 rounded-lg p-4 text-center hover:border-[#E040A0]/60 hover:bg-[#E040A0]/[0.04] transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadBaseImage(e.target.files[0])} data-testid="inpaint-base-input" />
                      {uploadingBase ? <Loader2 className="w-5 h-5 text-[#E040A0] animate-spin mx-auto" /> : (
                        <>
                          <Upload className="w-5 h-5 text-[#E040A0] mx-auto mb-1" />
                          <div className="text-[10px] text-white/70">Upload image</div>
                          <div className="text-[9px] text-white/30 mt-0.5">PNG/JPG · max 10MB</div>
                        </>
                      )}
                    </div>
                  </label>
                )}
              </div>
            )}

            {/* Tool selection */}
            <div>
              <div className="text-[10px] font-bold tracking-[0.3em] text-white/50 uppercase mb-2">Tool</div>
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={() => setTool('brush')} data-testid="tool-brush"
                  className={`p-2.5 rounded-lg flex items-center justify-center gap-1.5 text-xs transition-colors ${tool === 'brush' ? 'bg-[#FF6B4A]/20 text-[#FF6B4A] border border-[#FF6B4A]/30' : 'bg-white/[0.04] text-white/60 hover:text-white border border-white/[0.05]'}`}>
                  <Brush className="w-3.5 h-3.5" /> Brush
                </button>
                <button onClick={() => setTool('eraser')} data-testid="tool-eraser"
                  className={`p-2.5 rounded-lg flex items-center justify-center gap-1.5 text-xs transition-colors ${tool === 'eraser' ? 'bg-[#FF6B4A]/20 text-[#FF6B4A] border border-[#FF6B4A]/30' : 'bg-white/[0.04] text-white/60 hover:text-white border border-white/[0.05]'}`}>
                  <Eraser className="w-3.5 h-3.5" /> Eraser
                </button>
              </div>
            </div>

            {/* Brush size */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-[0.3em] text-white/50 uppercase">Size</span>
                <span className="text-xs text-white/80 font-mono">{brushSize}px</span>
              </div>
              <div className="grid grid-cols-6 gap-1">
                {BRUSH_SIZES.map((s) => (
                  <button key={s} onClick={() => setBrushSize(s)} data-testid={`brush-${s}`}
                    className={`aspect-square rounded-md flex items-center justify-center transition-colors ${brushSize === s ? 'bg-[#FF6B4A]/20 border border-[#FF6B4A]/30' : 'bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.05]'}`}>
                    <div className="rounded-full bg-white" style={{ width: Math.min(s/2, 18), height: Math.min(s/2, 18) }} />
                  </button>
                ))}
              </div>
            </div>

            {/* Color palette */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-[0.3em] text-white/50 uppercase">Color</span>
                <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
                  data-testid="color-picker"
                  className="w-7 h-7 rounded-md border border-white/[0.1] bg-transparent cursor-pointer" />
              </div>
              <div className="grid grid-cols-6 gap-1">
                {PALETTE.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                    title={c}
                    className={`aspect-square rounded-md border-2 transition-transform hover:scale-110 ${color === c ? 'border-[#FF6B4A]' : 'border-white/[0.1]'}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>

            {/* Background */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-bold tracking-[0.3em] text-white/50 uppercase">Background</span>
                <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)}
                  data-testid="bg-color-picker"
                  className="w-7 h-7 rounded-md border border-white/[0.1] bg-transparent cursor-pointer" />
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-1.5">
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={undo} disabled={historyIdx <= 0} data-testid="undo-btn"
                  className="p-2 rounded-lg bg-white/[0.04] border border-white/[0.05] text-xs text-white/70 hover:bg-white/[0.08] disabled:opacity-30 flex items-center justify-center gap-1">
                  <Undo2 className="w-3.5 h-3.5" /> Undo
                </button>
                <button onClick={redo} disabled={historyIdx >= history.length - 1} data-testid="redo-btn"
                  className="p-2 rounded-lg bg-white/[0.04] border border-white/[0.05] text-xs text-white/70 hover:bg-white/[0.08] disabled:opacity-30 flex items-center justify-center gap-1">
                  <Redo2 className="w-3.5 h-3.5" /> Redo
                </button>
              </div>
              <button onClick={clear} data-testid="clear-btn"
                className="w-full p-2 rounded-lg bg-white/[0.04] border border-white/[0.05] text-xs text-white/70 hover:bg-[#EF4444]/15 hover:text-[#EF4444] hover:border-[#EF4444]/30 flex items-center justify-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Clear
              </button>
            </div>

            {/* Pro tip */}
            <div className="p-3 rounded-lg bg-gradient-to-br from-[#FF6B4A]/10 to-[#E040A0]/10 border border-[#FF6B4A]/20">
              <div className="text-[10px] font-bold tracking-wider uppercase text-[#FF6B4A] mb-1">Pro tip</div>
              <p className="text-[10px] text-white/60 leading-relaxed">Rough shapes are enough. AI will fill in details based on your prompt.</p>
            </div>
          </aside>

          {/* Canvas */}
          <div ref={containerRef} className="flex-1 flex items-center justify-center bg-[#06060A] p-4 lg:p-8 overflow-hidden">
            <canvas
              ref={canvasRef}
              onMouseDown={startStroke}
              onMouseMove={draw}
              onMouseUp={endStroke}
              onMouseLeave={endStroke}
              onTouchStart={startStroke}
              onTouchMove={draw}
              onTouchEnd={endStroke}
              data-testid="sketch-canvas"
              className="rounded-lg shadow-2xl cursor-crosshair max-w-full max-h-full"
              style={{ background: bgColor, touchAction: 'none' }}
            />
          </div>
        </div>

        {/* Bottom prompt bar */}
        <div className="border-t border-white/[0.05] bg-[#08080C] p-4 lg:p-6 shrink-0">
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <div className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-2xl p-3 focus-within:border-[#FF6B4A]/40 transition-colors">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={mode === 'inpaint' ? 'Describe what to add in the painted area — e.g. "a red sports car"' : 'Describe what this sketch should become — e.g. "a watercolor painting of a sleeping cat"'}
                rows={2}
                data-testid="sketch-prompt"
                className="w-full bg-transparent text-white text-sm placeholder:text-white/30 focus:outline-none resize-none leading-relaxed"
              />
              <div className="text-[10px] text-white/40 mt-1">{mode === 'inpaint' ? 'Painted region will be replaced. Rest of image stays.' : 'Sketch becomes the reference. Prompt steers the style.'}</div>
            </div>
            <Button
              onClick={sendToStudio}
              disabled={sending}
              data-testid="send-to-studio"
              className="h-14 lg:px-6 bg-gradient-to-br from-[#FF6B4A] to-[#E040A0] text-white font-semibold rounded-2xl hover:from-[#FF8E72] hover:to-[#FF6B9D] disabled:opacity-40 shadow-lg shadow-[#FF6B4A]/20">
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 lg:mr-1.5" /><span className="hidden lg:inline">Generate</span></>}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}

function Sidebar({ location, user }) {
  return (
    <aside className="w-[68px] lg:w-[200px] border-r border-white/[0.05] bg-[#08080C] flex flex-col shrink-0">
      <Link to="/" className="h-16 flex items-center justify-center lg:justify-start lg:px-5 border-b border-white/[0.05]">
        <span className="font-body text-xl font-extrabold tracking-tight text-white">D<span className="text-[#FF6B4A]">2</span><span className="hidden lg:inline">ORT</span></span>
      </Link>
      <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
        {NAV_ITEMS.map((it) => {
          const active = location.pathname === it.path;
          return (
            <Link key={it.id} to={it.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-[#FF6B4A]/15 text-[#FF6B4A]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'}`}>
              <it.icon className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">{it.label}</span>
            </Link>
          );
        })}
        <div className="pt-4 mt-4 border-t border-white/[0.05] space-y-1">
          <Link to="/models" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-[#FFD93D] hover:bg-[#FFD93D]/[0.06]">
            <Cpu className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">150 Models</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <DollarSign className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Pricing</span>
          </Link>
          <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]">
            <Sparkles className="w-4 h-4 shrink-0" /><span className="hidden lg:inline">Credits</span>
          </Link>
        </div>
      </nav>
      {user && (
        <div className="hidden lg:flex items-center gap-2 px-3 pb-4 text-white/60">
          {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[#FF6B4A]/30" />}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-white truncate">{user.name?.split(' ')[0]}</div>
            <div className="text-[9px] text-white/50">{formatCredits(user)} cr</div>
          </div>
        </div>
      )}
    </aside>
  );
}
